import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    api.get(`/auth/reset-password/${token}`)
      .then(({ data }) => { setValid(true); setEmail(data.email || ""); })
      .catch(() => setValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated — you can sign in now");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Couldn't reset password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-secondary/40">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-xl" data-testid="reset-password-card">
        <Link to="/" className="inline-flex items-center mb-8">
          <img src="/logo-light.png" alt="VividVR" className="h-9 w-auto" />
        </Link>

        {checking ? (
          <div className="flex items-center gap-2 text-muted-foreground py-6"><Loader2 className="w-5 h-5 animate-spin" /> Checking your reset link…</div>
        ) : !valid ? (
          <div data-testid="reset-invalid">
            <h1 className="text-2xl font-display font-bold">Link expired</h1>
            <p className="text-muted-foreground mt-2 text-sm">This password reset link is invalid or has expired. Ask your administrator to send a new one.</p>
            <Button onClick={() => nav("/login")} className="w-full h-11 rounded-full mt-6">Back to sign in</Button>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-done">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <h1 className="text-2xl font-display font-bold mt-3">All set!</h1>
            <p className="text-muted-foreground mt-2 text-sm">Your password has been updated.</p>
            <Button onClick={() => nav("/login")} data-testid="reset-signin-btn" className="w-full h-11 rounded-full mt-6">Sign in</Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-primary" /><h1 className="text-2xl font-display font-bold">Set a new password</h1></div>
            <p className="text-muted-foreground mt-2 text-sm">for {email}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="pw">New password</Label>
                <Input id="pw" data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" required />
              </div>
              <div>
                <Label htmlFor="pw2">Confirm password</Label>
                <Input id="pw2" data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-11" required />
              </div>
              <Button type="submit" data-testid="reset-submit-btn" disabled={saving} className="w-full h-11 rounded-full text-base">
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
